import React, { useState } from 'react';
import { Plus, ExternalLink, AlertCircle } from 'lucide-react';
import { MessageCircle, Send, Gamepad2, Globe, Download, Link } from 'lucide-react';

const DigitalLinkForm = ({ onLinkAdd }) => {
    const [showForm, setShowForm] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkType, setLinkType] = useState('WEBSITE');
    const [linkDescription, setLinkDescription] = useState('');
    const [originalName, setOriginalName] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    const linkTypes = [
        { value: 'WHATSAPP_GROUP', label: 'WhatsApp Group', icon: <MessageCircle className="w-4 h-4" /> },
        { value: 'TELEGRAM_GROUP', label: 'Telegram Group', icon: <Send className="w-4 h-4" /> },
        { value: 'DISCORD_SERVER', label: 'Discord Server', icon: <Gamepad2 className="w-4 h-4" /> },
        { value: 'WEBSITE', label: 'Website', icon: <Globe className="w-4 h-4" /> },
        { value: 'DOWNLOAD_LINK', label: 'Download Link', icon: <Download className="w-4 h-4" /> },
        { value: 'OTHER', label: 'Other', icon: <Link className="w-4 h-4" /> }
    ];

    // URL validation rules
    const allowedDomains = {
        WHATSAPP_GROUP: ['chat.whatsapp.com', 'wa.me'],
        TELEGRAM_GROUP: ['t.me', 'telegram.me'],
        DISCORD_SERVER: ['discord.gg', 'discord.com'],
        WEBSITE: [],
        DOWNLOAD_LINK: [],
        OTHER: []
    };

    const validateUrl = (url, linkType) => {
        try {
            const parsedUrl = new URL(url);

            if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
                return { isValid: false, error: 'URL must use HTTP or HTTPS protocol' };
            }

            const allowedDomainsList = allowedDomains[linkType];
            if (allowedDomainsList && allowedDomainsList.length > 0) {
                const isAllowedDomain = allowedDomainsList.some(domain =>
                    parsedUrl.hostname === domain || parsedUrl.hostname.endsWith('.' + domain)
                );

                if (!isAllowedDomain) {
                    return {
                        isValid: false,
                        error: `URL domain not allowed for ${linkType}. Allowed domains: ${allowedDomainsList.join(', ')}`
                    };
                }
            }

            return { isValid: true };
        } catch (error) {
            return { isValid: false, error: 'Invalid URL format' };
        }
    };

    const handleUrlChange = (e) => {
        const url = e.target.value;
        setLinkUrl(url);
        setValidationError('');

        if (url.trim()) {
            setIsValidating(true);
            setTimeout(() => {
                const validation = validateUrl(url.trim(), linkType);
                if (!validation.isValid) {
                    setValidationError(validation.error);
                }
                setIsValidating(false);
            }, 500);
        }
    };

    const handleLinkTypeChange = (e) => {
        const newLinkType = e.target.value;
        setLinkType(newLinkType);
        setValidationError('');

        if (linkUrl.trim()) {
            const validation = validateUrl(linkUrl.trim(), newLinkType);
            if (!validation.isValid) {
                setValidationError(validation.error);
            }
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (!linkUrl.trim()) {
            setValidationError('URL is required');
            return;
        }

        const validation = validateUrl(linkUrl.trim(), linkType);
        if (!validation.isValid) {
            setValidationError(validation.error);
            return;
        }

        const newLink = {
            linkUrl: linkUrl.trim(),
            linkType,
            linkDescription: linkDescription.trim(),
            originalName: originalName.trim() || `${linkType.toLowerCase()}_link`
        };

        onLinkAdd(newLink);

        // Reset form
        setLinkUrl('');
        setLinkType('WHATSAPP_GROUP');
        setLinkDescription('');
        setOriginalName('');
        setValidationError('');
        setShowForm(false);
    };

    const handleCancel = () => {
        setLinkUrl('');
        setLinkType('WHATSAPP_GROUP');
        setLinkDescription('');
        setOriginalName('');
        setValidationError('');
        setShowForm(false);
    };

    const getPlaceholderUrl = (linkType) => {
        switch (linkType) {
            case 'WHATSAPP_GROUP':
                return 'https://chat.whatsapp.com/AbCdEfGhIjKlMnOpQrSt';
            case 'TELEGRAM_GROUP':
                return 'https://t.me/your_group_name';
            case 'DISCORD_SERVER':
                return 'https://discord.gg/AbCdEfGh';
            case 'WEBSITE':
                return 'https://example.com';
            case 'DOWNLOAD_LINK':
                return 'https://drive.google.com/file/...';
            default:
                return 'https://...';
        }
    };

    const isUrlValid = linkUrl.trim() && !validationError && !isValidating;
    const selectedLinkType = linkTypes.find(type => type.value === linkType);

    if (!showForm) {
        return (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <button
                    type="button"
                    onClick={() => setShowForm(true)}
                    className="w-full bg-transparent flex flex-col items-center justify-center text-center transition-colors rounded-lg p-2"
                >
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                        <Plus className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Add Digital Link</p>
                    <p className="text-xs text-gray-500">
                        WhatsApp groups, websites, download links, etc.
                    </p>
                </button>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                        <ExternalLink className="w-3 h-3 text-blue-600" />
                    </div>
                    <h4 className="text-sm font-medium text-gray-900">Add Link</h4>
                </div>
                <button
                    type="button"
                    onClick={handleCancel}
                    className="p-1 bg-white text-gray-400 hover:text-gray-600 transition-colors"
                >
                    ✕
                </button>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link Type
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {linkTypes.map(type => (
                            <button
                                key={type.value}
                                type="button"
                                onClick={() => handleLinkTypeChange({ target: { value: type.value } })}
                                className={`p-2 bg-gray-50 rounded-lg border-2 transition-all text-center ${linkType === type.value
                                    ? 'border-[#191970] bg-[#191970]/5 text-[#191970]'
                                    : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                    }`}
                            >
                                <div className="text-sm mb-1">{type.icon}</div>
                                <div className="text-xs font-medium">{type.label}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        URL *
                    </label>
                    <input
                        type="url"
                        value={linkUrl}
                        onChange={handleUrlChange}
                        placeholder={getPlaceholderUrl(linkType)}
                        className={`w-full bg-white px-3 py-2 text-sm border rounded-lg focus:ring-1 focus:ring-opacity-50 transition-colors ${validationError
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:border-[#191970] focus:ring-[#191970]'
                            }`}
                    />

                    {isValidating && (
                        <div className="mt-1 flex items-center text-xs text-gray-500">
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400 mr-2"></div>
                            Validating...
                        </div>
                    )}

                    {validationError && (
                        <div className="mt-1 flex items-start text-xs text-red-600">
                            <AlertCircle className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0" />
                            <span>{validationError}</span>
                        </div>
                    )}

                    {isUrlValid && (
                        <div className="mt-1 flex items-center text-xs text-green-600">
                            <div className="w-3 h-3 rounded-full bg-green-100 flex items-center justify-center mr-1">
                                <span className="text-[8px]">✓</span>
                            </div>
                            Valid URL
                        </div>
                    )}

                    {allowedDomains[linkType]?.length > 0 && (
                        <div className="mt-1 text-xs text-gray-500">
                            Allowed: {allowedDomains[linkType].join(', ')}
                        </div>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                    </label>
                    <input
                        type="text"
                        value={linkDescription}
                        onChange={(e) => setLinkDescription(e.target.value)}
                        placeholder="What customers will access..."
                        className="w-full bg-white px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-[#191970] focus:ring-1 focus:ring-[#191970] transition-colors"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name
                    </label>
                    <input
                        type="text"
                        value={originalName}
                        onChange={(e) => setOriginalName(e.target.value)}
                        placeholder="Custom name for this link"
                        className="w-full bg-white px-3 py-2 text-sm border border-gray-300 rounded-lg focus:border-[#191970] focus:ring-1 focus:ring-[#191970] transition-colors"
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-800 text-sm rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!isUrlValid}
                        className="w-full sm:w-auto px-4 py-2 bg-[#191970] text-white text-sm rounded-lg hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        Add Link
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DigitalLinkForm;